import { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, ShieldAlert, Trash2, Sparkles, AlertTriangle, History } from "lucide-react";
import { toast } from "sonner";

export default function ResetOperacional() {
  const { user } = useAuth();
  const [opts, setOpts] = useState(null);
  const [tenantId, setTenantId] = useState("");
  const [demo, setDemo] = useState(null);
  const [historico, setHistorico] = useState([]);

  // seleção OPERACAO
  const [grupos, setGrupos] = useState({});
  const [reinicioOp, setReinicioOp] = useState(false);

  // dialogos
  const [demoDlg, setDemoDlg] = useState(false);
  const [demoSenha, setDemoSenha] = useState("");
  const [demoReinicio, setDemoReinicio] = useState(false);

  const [opStep, setOpStep] = useState(0); // 0 fechado, 1 aviso, 2 confirmacao
  const [opConfirm, setOpConfirm] = useState("");
  const [opSenha, setOpSenha] = useState("");
  const [busy, setBusy] = useState(false);

  // Reset Geral do Ambiente
  const [geral, setGeral] = useState(null);
  const [geralStep, setGeralStep] = useState(0); // 0 fechado, 1 aviso, 2 confirmacao
  const [geralConfirm, setGeralConfirm] = useState("");
  const [geralSenha, setGeralSenha] = useState("");

  const tenantNome = useMemo(
    () => opts?.tenants.find((t) => t.id === tenantId)?.nome || "",
    [opts, tenantId]
  );

  const loadHist = () => api.get("/admin/reset/historico").then((r) => setHistorico(r.data));
  const loadDemo = (tid) => api.get("/admin/reset/demo-count", { params: { tenant_id: tid } }).then((r) => setDemo(r.data));
  const loadGeral = (tid) => api.get("/admin/reset/geral-count", { params: { tenant_id: tid } }).then((r) => setGeral(r.data));

  useEffect(() => {
    api.get("/admin/reset/opcoes").then((r) => {
      setOpts(r.data);
      setTenantId(r.data.tenant_id);
      const g = {}; r.data.grupos.forEach((x) => (g[x.id] = false)); setGrupos(g);
    });
    loadHist();
  }, []);

  useEffect(() => { if (tenantId) { loadDemo(tenantId); loadGeral(tenantId); } }, [tenantId]);

  const toggleGrupo = (id) => setGrupos((g) => ({ ...g, [id]: !g[id] }));
  const selecionarTudo = () => setGrupos((g) => Object.fromEntries(Object.keys(g).map((k) => [k, true])));
  const selecionados = Object.entries(grupos).filter(([, v]) => v).map(([k]) => k);

  const execDemo = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/reset", {
        tenant_id: tenantId, modo: "DEMO", grupos: [], reiniciar_numeracao: demoReinicio, senha: demoSenha,
      });
      toast.success(`Dados de demonstração removidos: ${data.resultado.total_removido} registro(s)`);
      setDemoDlg(false); setDemoSenha(""); setDemoReinicio(false);
      loadDemo(tenantId); loadHist();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao limpar dados demo"); }
    finally { setBusy(false); }
  };

  const execOperacao = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/reset", {
        tenant_id: tenantId, modo: "OPERACAO", grupos: selecionados,
        reiniciar_numeracao: reinicioOp, senha: opSenha, confirmacao: opConfirm,
      });
      toast.success(`Operação resetada: ${data.resultado.total_removido} registro(s) removido(s)`);
      setOpStep(0); setOpConfirm(""); setOpSenha(""); setReinicioOp(false);
      setGrupos((g) => Object.fromEntries(Object.keys(g).map((k) => [k, false])));
      loadDemo(tenantId); loadHist();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao resetar operação"); }
    finally { setBusy(false); }
  };

  const execGeral = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/reset", {
        tenant_id: tenantId, modo: "GERAL", grupos: [], reiniciar_numeracao: true,
        senha: geralSenha, confirmacao: geralConfirm,
      });
      toast.success(`Ambiente zerado: ${data.resultado.total_removido} registro(s) removido(s)`);
      setGeralStep(0); setGeralConfirm(""); setGeralSenha("");
      loadDemo(tenantId); loadGeral(tenantId); loadHist();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao resetar ambiente"); }
    finally { setBusy(false); }
  };

  if (user.role !== "SUPER_ADMIN") return (
    <div className="max-w-lg mx-auto py-20 text-center space-y-3" data-testid="reset-restrito">
      <ShieldAlert className="w-10 h-10 text-amber-400 mx-auto" />
      <div className="font-display text-2xl font-extrabold uppercase">Acesso restrito</div>
      <p className="text-sm text-muted-foreground">As ferramentas de reset e manutenção são exclusivas do Administrador Geral da plataforma NEXO LOG.</p>
    </div>
  );

  if (!opts) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  const confirmTexto = `RESETAR ${tenantNome}`;
  const confirmOk = opConfirm.trim() === confirmTexto && opSenha.length > 0;
  const geralOk = geralConfirm.trim() === confirmTexto && geralSenha.length > 0;

  return (
    <div className="space-y-6" data-testid="reset-page">
      <div>
        <div className="text-xs text-primary uppercase tracking-[0.18em] font-bold">Administração</div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold uppercase flex items-center gap-2">
          <ShieldAlert className="w-8 h-8 text-primary" /> Reset Operacional
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Limpe dados de teste/demonstração, zere grupos operacionais reais ou execute o <b>Reset Geral do Ambiente</b> para reiniciar tudo do zero (mantendo empresa e usuários).
        </p>
      </div>

      {/* Seletor de empresa (Super Admin) */}
      {opts.is_super && (
        <Card className="bg-card/70 p-4">
          <Label className="text-xs">Empresa (tenant)</Label>
          <Select value={tenantId} onValueChange={setTenantId}>
            <SelectTrigger className="max-w-md mt-1" data-testid="reset-tenant-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {opts.tenants.map((t) => <SelectItem key={t.id} value={t.id}>{t.nome}</SelectItem>)}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground mt-2">O reset afeta somente a empresa selecionada. Nunca atinge outros tenants.</p>
        </Card>
      )}
      {!opts.is_super && (
        <Card className="bg-card/70 p-4">
          <div className="text-xs text-muted-foreground">Empresa</div>
          <div className="font-display text-lg font-bold">{tenantNome}</div>
        </Card>
      )}

      {/* 1. Limpar dados de demonstração */}
      <Card className="bg-card/70 p-5 border-primary/30" data-testid="reset-demo-card">
        <div className="flex items-start gap-3">
          <Sparkles className="w-6 h-6 text-primary shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-display text-lg font-bold uppercase">Limpar dados de demonstração</div>
            <p className="text-sm text-muted-foreground mt-1">
              Remove exclusivamente os registros marcados como demonstração ({demo ? demo.total : "…"} registro(s)).
              Nenhum dado operacional real criado por você é afetado.
            </p>
          </div>
          <Button variant="secondary" disabled={!demo || demo.total === 0} onClick={() => setDemoDlg(true)} data-testid="btn-open-demo">
            <Trash2 className="w-4 h-4" /> Limpar demonstração
          </Button>
        </div>
      </Card>

      {/* 2. Resetar operação */}
      <Card className="bg-card/70 p-5 border-destructive/40" data-testid="reset-op-card">
        <div className="flex items-center gap-2 mb-3">
          <AlertTriangle className="w-6 h-6 text-destructive" />
          <div className="font-display text-lg font-bold uppercase">Resetar operação (crítico)</div>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Zera permanentemente os grupos de dados operacionais <b>reais</b> selecionados desta empresa. Exige dupla confirmação e senha.
        </p>
        <div className="grid sm:grid-cols-2 gap-2 mb-4">
          {opts.grupos.map((g) => (
            <label key={g.id} className="flex items-center gap-2 text-sm cursor-pointer bg-secondary/40 rounded-md px-3 py-2">
              <Checkbox checked={!!grupos[g.id]} onCheckedChange={() => toggleGrupo(g.id)} data-testid={`grupo-${g.id}`} />
              {g.label}
            </label>
          ))}
        </div>
        <div className="flex items-center flex-wrap gap-4">
          <Button variant="outline" size="sm" onClick={selecionarTudo} data-testid="btn-select-all">Resetar toda a operação</Button>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox checked={reinicioOp} onCheckedChange={(v) => setReinicioOp(!!v)} data-testid="reinicio-op" />
            Reiniciar sequências de documentos
          </label>
          <Button variant="destructive" className="ml-auto" disabled={selecionados.length === 0}
            onClick={() => setOpStep(1)} data-testid="btn-continuar-reset">
            Continuar
          </Button>
        </div>
      </Card>

      {/* 3. Reset Geral do Ambiente */}
      <Card className="bg-card/70 p-5 border-destructive/60 ring-1 ring-destructive/30" data-testid="reset-geral-card">
        <div className="flex items-center gap-2 mb-3">
          <ShieldAlert className="w-6 h-6 text-destructive" />
          <div className="font-display text-lg font-bold uppercase text-destructive">Reset Geral do Ambiente</div>
        </div>
        <p className="text-sm text-muted-foreground mb-2">
          Zera <b>tudo</b> desta empresa como se o sistema estivesse sendo usado pela primeira vez: pedidos, movimentações,
          saldos, inventários, ocorrências/avarias, devoluções, compras, <b>produtos</b>, categorias, fornecedores,
          <b> unidades e o CD</b>, fotos e sequências de numeração. Seus usuários e o cadastro da empresa são mantidos.
        </p>
        <div className="text-xs text-muted-foreground mb-4">
          {geral ? <>Registros que serão removidos: <b className="text-foreground">{geral.total}</b></> : "…"}
        </div>
        <div className="flex justify-end">
          <Button variant="destructive" disabled={!geral || geral.total === 0}
            onClick={() => setGeralStep(1)} data-testid="btn-continuar-geral">
            <Trash2 className="w-4 h-4" /> Resetar ambiente completo
          </Button>
        </div>
      </Card>

      {/* Histórico */}
      <Card className="bg-card/70 p-5" data-testid="reset-historico">
        <div className="flex items-center gap-2 mb-3"><History className="w-5 h-5 text-primary" /><div className="font-display text-lg font-bold uppercase">Auditoria de Resets</div></div>
        {historico.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nenhum reset registrado.</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Data/Hora</TableHead><TableHead>Empresa</TableHead><TableHead>Modo</TableHead>
                <TableHead>Grupos</TableHead><TableHead>Removidos</TableHead><TableHead>Executado por</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {historico.map((h) => (
                  <TableRow key={h.id} data-testid={`hist-${h.id}`}>
                    <TableCell className="whitespace-nowrap">{new Date(h.timestamp).toLocaleString("pt-BR")}</TableCell>
                    <TableCell>{h.tenant_nome}</TableCell>
                    <TableCell><span className={h.modo === "OPERACAO" ? "text-destructive font-semibold" : "text-primary"}>{h.modo}</span></TableCell>
                    <TableCell className="text-xs">{(h.grupos || []).join(", ")}{h.numeracao_reiniciada ? " + numeração" : ""}</TableCell>
                    <TableCell>{h.total_removido}</TableCell>
                    <TableCell className="text-xs">{h.usuario_nome}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      {/* Dialog DEMO */}
      <Dialog open={demoDlg} onOpenChange={(o) => { if (!o) { setDemoDlg(false); setDemoSenha(""); } }}>
        <DialogContent data-testid="demo-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-primary" /> Limpar dados de demonstração</DialogTitle>
            <DialogDescription>
              Serão removidos {demo?.total} registro(s) de demonstração de <b>{tenantNome}</b>. Dados reais não são afetados. Confirme com sua senha.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Sua senha</Label>
              <Input type="password" value={demoSenha} onChange={(e) => setDemoSenha(e.target.value)} data-testid="demo-senha" />
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox checked={demoReinicio} onCheckedChange={(v) => setDemoReinicio(!!v)} data-testid="demo-reinicio" />
              Reiniciar sequências de documentos
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDemoDlg(false); setDemoSenha(""); }}>Cancelar</Button>
            <Button variant="destructive" disabled={busy || !demoSenha} onClick={execDemo} data-testid="demo-confirm">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Trash2 className="w-4 h-4" /> Limpar</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog OPERACAO - passo 1 aviso */}
      <Dialog open={opStep === 1} onOpenChange={(o) => { if (!o) setOpStep(0); }}>
        <DialogContent data-testid="op-dialog-1">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive"><AlertTriangle className="w-5 h-5" /> Atenção</DialogTitle>
            <DialogDescription>
              Esta operação excluirá permanentemente os dados operacionais selecionados da empresa <b>{tenantNome}</b>. Esta ação não poderá ser desfeita.
            </DialogDescription>
          </DialogHeader>
          <div className="text-sm bg-secondary/40 rounded-md p-3">
            <div className="text-xs text-muted-foreground mb-1">Grupos selecionados:</div>
            {selecionados.map((id) => opts.grupos.find((g) => g.id === id)?.label).join(" · ")}
            {reinicioOp && <div className="text-primary mt-1">+ Reiniciar sequências de documentos</div>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpStep(0)}>Cancelar</Button>
            <Button variant="destructive" onClick={() => setOpStep(2)} data-testid="op-continuar">Continuar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog OPERACAO - passo 2 confirmacao textual + senha */}
      <Dialog open={opStep === 2} onOpenChange={(o) => { if (!o) { setOpStep(0); setOpConfirm(""); setOpSenha(""); } }}>
        <DialogContent data-testid="op-dialog-2">
          <DialogHeader>
            <DialogTitle className="text-destructive">Confirmação final</DialogTitle>
            <DialogDescription>
              Para confirmar, digite exatamente <span className="font-mono font-bold text-foreground">{confirmTexto}</span> e informe sua senha.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Digite: {confirmTexto}</Label>
              <Input value={opConfirm} onChange={(e) => setOpConfirm(e.target.value)} placeholder={confirmTexto} data-testid="op-confirm-input" />
            </div>
            <div>
              <Label className="text-xs">Sua senha</Label>
              <Input type="password" value={opSenha} onChange={(e) => setOpSenha(e.target.value)} data-testid="op-senha-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setOpStep(0); setOpConfirm(""); setOpSenha(""); }}>Cancelar</Button>
            <Button variant="destructive" disabled={!confirmOk || busy} onClick={execOperacao} data-testid="op-final-confirm">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Trash2 className="w-4 h-4" /> RESETAR OPERAÇÃO</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Dialog GERAL - passo 1 aviso */}
      <Dialog open={geralStep === 1} onOpenChange={(o) => { if (!o) setGeralStep(0); }}>
        <DialogContent data-testid="geral-dialog-1">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive"><ShieldAlert className="w-5 h-5" /> Reset Geral do Ambiente</DialogTitle>
            <DialogDescription>
              Isto excluirá <b>permanentemente</b> todos os dados e cadastros de <b>{tenantNome}</b> — incluindo produtos,
              unidades e o CD. Somente usuários e a empresa serão mantidos. Esta ação não pode ser desfeita.
            </DialogDescription>
          </DialogHeader>
          {geral && (
            <div className="text-sm bg-secondary/40 rounded-md p-3 max-h-48 overflow-y-auto">
              {Object.entries(geral.counts).filter(([, n]) => n > 0).map(([c, n]) => (
                <div key={c} className="flex justify-between"><span className="text-muted-foreground">{c}</span><span className="font-mono">{n}</span></div>
              ))}
              <div className="flex justify-between border-t border-border mt-2 pt-2 font-bold"><span>Total</span><span className="font-mono">{geral.total}</span></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setGeralStep(0)}>Cancelar</Button>
            <Button variant="destructive" onClick={() => setGeralStep(2)} data-testid="geral-continuar">Continuar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog GERAL - passo 2 confirmacao textual + senha */}
      <Dialog open={geralStep === 2} onOpenChange={(o) => { if (!o) { setGeralStep(0); setGeralConfirm(""); setGeralSenha(""); } }}>
        <DialogContent data-testid="geral-dialog-2">
          <DialogHeader>
            <DialogTitle className="text-destructive">Confirmação final</DialogTitle>
            <DialogDescription>
              Para zerar todo o ambiente, digite exatamente <span className="font-mono font-bold text-foreground">{confirmTexto}</span> e informe sua senha.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Digite: {confirmTexto}</Label>
              <Input value={geralConfirm} onChange={(e) => setGeralConfirm(e.target.value)} placeholder={confirmTexto} data-testid="geral-confirm-input" />
            </div>
            <div>
              <Label className="text-xs">Sua senha</Label>
              <Input type="password" value={geralSenha} onChange={(e) => setGeralSenha(e.target.value)} data-testid="geral-senha-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setGeralStep(0); setGeralConfirm(""); setGeralSenha(""); }}>Cancelar</Button>
            <Button variant="destructive" disabled={!geralOk || busy} onClick={execGeral} data-testid="geral-final-confirm">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Trash2 className="w-4 h-4" /> RESETAR AMBIENTE</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
